import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, ImageIcon } from "lucide-react";
import { COMMISSION_CONFIG as C } from "../../data/commissionConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferenceUpload({ images, setImages }) {
  const inputRef = useRef(null);
  const [busyCount, setBusyCount] = useState(0);
  const remaining = C.maxUploads - images.length;

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, remaining);
    if (!files.length) return;
    setBusyCount((n) => n + files.length);
    await Promise.all(
      files.map(async (file) => {
        if (file.size > C.maxUploadMb * 1024 * 1024) {
          toast.error(`${file.name} is over ${C.maxUploadMb} MB.`);
          return;
        }
        const preview = URL.createObjectURL(file);
        const fd = new FormData();
        fd.append("file", file);
        try {
          const { data } = await axios.post(`${API}/commission-estimate/upload`, fd);
          setImages((prev) =>
            prev.length < C.maxUploads
              ? [...prev, { id: data.id, url: data.url, filename: file.name, preview }]
              : prev
          );
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Couldn't upload ${file.name}.`);
        }
      })
    );
    setBusyCount((n) => Math.max(0, n - files.length));
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => setImages((prev) => prev.filter((i) => i.id !== id));

  return (
    <div data-testid="reference-upload">
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 md:gap-4">
        {images.map((img) => (
          <div key={img.id} className="relative aspect-square bg-[hsl(var(--surface))] overflow-hidden" data-testid={`reference-thumb-${img.id}`}>
            <Thumb img={img} />
            <button
              type="button"
              onClick={() => remove(img.id)}
              aria-label={`Remove ${img.filename}`}
              className="absolute top-1.5 right-1.5 h-8 w-8 md:h-7 md:w-7 bg-foreground text-background flex items-center justify-center hover:bg-[hsl(var(--accent))] transition-colors"
              data-testid={`reference-remove-${img.id}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {Array.from({ length: busyCount }).map((_, i) => (
          <div key={`busy-${i}`} className="aspect-square bg-[hsl(var(--surface))] animate-pulse" data-testid="reference-uploading" />
        ))}
        {remaining - busyCount > 0 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="aspect-square border border-dashed border-[hsl(var(--ink-soft))]/50 flex flex-col items-center justify-center gap-2 text-[hsl(var(--ink-soft))] hover:border-foreground hover:text-foreground transition-colors"
            data-testid="reference-add-button"
          >
            <Plus className="h-5 w-5" />
            <span className="text-[0.625rem] uppercase tracking-[0.18em]">Add image</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={C.acceptedTypes}
        className="hidden"
        onChange={(e) => onFiles(e.target.files)}
        data-testid="reference-file-input"
      />
      <p className="mt-3 text-xs text-[hsl(var(--ink-soft))]">
        {images.length} of {C.maxUploads} · JPG, PNG, WEBP, or HEIC · up to {C.maxUploadMb} MB each
      </p>
    </div>
  );
}

function Thumb({ img }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 text-[hsl(var(--ink-soft))]">
        <ImageIcon className="h-5 w-5" />
        <span className="text-[0.625rem] text-center truncate w-full">{img.filename}</span>
      </div>
    );
  }
  return (
    <img src={img.preview} alt={img.filename} className="w-full h-full object-cover" onError={() => setBroken(true)} />
  );
}
